// Step 3b: the chosen day drawn across its opening hours — the bands in which
// the guest's whole choice can start, the stretch the notice rule rules out,
// and, when nothing is free, the true reason (and, on a day the chosen options
// can only have one at a time, the way out: emptyActions).

import type { KeyboardEvent, ReactNode } from "react";
import {
  anyStartBounds,
  bandStarts,
  dayLabel,
  formatLength,
  lengthAdjective,
  noticeReason,
  type Band,
  type DayStatus,
  type Granularity,
  type OffsetAt,
} from "../../state/availability-logic";
import { formatHm } from "../../state/tenant-time";

const HOUR_MS = 3_600_000;

type Window = { startMs: number; endMs: number };

export function DayTimeline({
  day,
  todayYmd,
  tz,
  offsetAt,
  windows,
  bands,
  status,
  noticeKnown,
  noticeMin,
  longestMin,
  gran,
  shape,
  entryCount,
  selectedBand,
  onSelectBand,
  emptyCopy,
  emptyActions,
  onRetry,
  onNextDay,
  disabled,
  nowMs,
  fixedStarts = false,
  bare = false,
}: {
  day: string;
  todayYmd: string;
  tz: string;
  offsetAt: OffsetAt;
  windows: Window[];
  bands: Band[];
  status: DayStatus;
  noticeKnown: boolean;
  noticeMin: number;
  longestMin: number;
  gran: Granularity;
  shape: "sessions" | "multi";
  entryCount: number;
  selectedBand: Band | null;
  onSelectBand: (band: Band) => void;
  emptyCopy: string | null;
  /** Buttons under emptyCopy ("All 4 riders on …"); shown only when nothing is free. */
  emptyActions?: ReactNode;
  onRetry: () => void;
  onNextDay?: () => void;
  disabled: boolean;
  /** The panel's clock: an opening that is only the now-floor is not a start (edgeChips). */
  nowMs?: number;
  /** Every band is one fixed start: the start picker lists them all, no window step. */
  fixedStarts?: boolean;
  /**
   * The day tiles list the start times themselves (day-tile-logic.ts): no
   * time strip. Only the day's words, its ways out and "Next day →" show.
   */
  bare?: boolean;
}) {
  const label = dayLabel(day, todayYmd);
  if (status === "idle") return null;
  if (status === "checking") {
    return (
      <div className="vt-avail-timeline">
        {!bare && <div className="vt-avail-bar vt-avail-bar-skeleton" aria-hidden="true" />}
        <p className="vt-avail-caption">Checking availability…</p>
      </div>
    );
  }
  if (status === "unknown") {
    return (
      <div className="vt-avail-timeline">
        <div className="vt-avail-empty" role="status">
          {emptyCopy ?? <>We couldn&rsquo;t check live availability for {label.short}. Pick a time and we&rsquo;ll
          confirm it when you add it.</>}{" "}
          <button type="button" className="vt-avail-link" onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const pickable = bands.filter((b) => bandStarts(b, gran, offsetAt, nowMs).length > 0);
  const floorHour = (ms: number) => {
    const off = offsetAt(ms) * 60_000;
    return Math.floor((ms + off) / HOUR_MS) * HOUR_MS - off;
  };
  const ceilHour = (ms: number) => {
    const off = offsetAt(ms) * 60_000;
    return Math.ceil((ms + off) / HOUR_MS) * HOUR_MS - off;
  };
  const domain: [number, number] | null = windows.length
    ? [Math.min(...windows.map((w) => w.startMs)), Math.max(...windows.map((w) => w.endMs))]
    : pickable.length
      ? [floorHour(pickable[0]!.opensAtMs), ceilHour(pickable[pickable.length - 1]!.lastStartMs + longestMin * 60_000)]
      : null;
  const span = domain ? Math.max(1, domain[1] - domain[0]) : 1;
  const pct = (ms: number) => (domain ? Math.min(100, Math.max(0, ((ms - domain[0]) / span) * 100)) : 0);

  const ticks: { ms: number; text: string; minor: boolean; end: boolean }[] = [];
  if (domain) {
    const hours = span / HOUR_MS;
    const step = hours <= 6 ? 1 : hours <= 12 ? 2 : 3;
    const first = ceilHour(domain[0]);
    let i = 0;
    for (let t = first; t <= domain[1] && i < 60; t += HOUR_MS, i++) {
      if (i % step !== 0) continue;
      ticks.push({ ms: t, text: formatHm(t, tz), minor: t !== first && t + step * HOUR_MS <= domain[1], end: t >= domain[1] });
    }
  }

  const windowOf = (ms: number) => windows.find((w) => w.startMs <= ms && ms <= w.endMs) ?? null;
  const noticeBand = pickable.find((b) => b.noticeTruncated) ?? null;
  const noticeFirstStart = noticeBand ? bandStarts(noticeBand, gran, offsetAt, nowMs)[0] ?? null : null;

  const bar = domain && !bare ? (
    <div className="vt-avail-bar" aria-hidden="true">
      {windows.map((w) => (
        <span
          key={w.startMs}
          className="vt-avail-open"
          style={{ left: `${pct(w.startMs)}%`, width: `${pct(w.endMs) - pct(w.startMs)}%` }}
        />
      ))}
      {status === "notice" &&
        windows.map((w) => (
          <span
            key={`n${w.startMs}`}
            className="vt-avail-notice"
            style={{ left: `${pct(w.startMs)}%`, width: `${pct(w.endMs) - pct(w.startMs)}%` }}
          />
        ))}
      {status === "free" &&
        pickable
          .filter((b) => b.noticeTruncated)
          .map((b) => {
            const from = windowOf(b.opensAtMs)?.startMs ?? domain[0];
            return (
              <span
                key={`h${b.opensAtMs}`}
                className="vt-avail-notice"
                style={{ left: `${pct(from)}%`, width: `${Math.max(0, pct(b.opensAtMs) - pct(from))}%` }}
              />
            );
          })}
      {status === "free" &&
        pickable.map((b) => {
          const on = selectedBand?.opensAtMs === b.opensAtMs;
          return (
            <button
              key={b.opensAtMs}
              type="button"
              tabIndex={-1}
              className={`vt-avail-band${on ? " vt-avail-band-on" : ""}`}
              style={{ left: `${pct(b.opensAtMs)}%`, width: `${Math.max(pct(b.lastStartMs) - pct(b.opensAtMs), 0)}%` }}
              onClick={() => {
                if (!disabled) onSelectBand(b);
              }}
            />
          );
        })}
    </div>
  ) : null;

  const tickRow = domain && !bare ? (
    <div className="vt-avail-ticks" aria-hidden="true">
      {ticks.map((t) => (
        <span
          key={t.ms}
          className={`vt-avail-tick${t.minor ? " vt-avail-tick-minor" : ""}${t.end ? " vt-avail-tick-end" : ""}`}
          style={{ left: `${pct(t.ms)}%` }}
        >
          {t.text}
        </span>
      ))}
    </div>
  ) : null;

  if (status !== "free") {
    return (
      <div className="vt-avail-timeline">
        {bar}
        {tickRow}
        {emptyCopy && (
          <p className="vt-avail-empty" role="status">
            {emptyCopy}
          </p>
        )}
        {emptyActions}
        {onNextDay && (
          <button type="button" className="vt-avail-link" onClick={onNextDay} disabled={disabled}>
            Next day →
          </button>
        )}
      </div>
    );
  }

  const bandText = (b: Band): { text: string; aria: string } => {
    if (gran === "any") {
      const x = anyStartBounds(b)!;
      const a = formatHm(x.minMs, tz);
      const z = formatHm(x.maxMs, tz);
      return a === z ? { text: a, aria: `Start at ${a}` } : { text: `${a}–${z}`, aria: `Start between ${a} and ${z}` };
    }
    const starts = bandStarts(b, gran, offsetAt, nowMs);
    const a = formatHm(starts[0]!, tz);
    const z = formatHm(starts[starts.length - 1]!, tz);
    return a === z ? { text: a, aria: `Start at ${a}` } : { text: `${a}–${z}`, aria: `Start between ${a} and ${z}` };
  };
  const selIndex = Math.max(0, pickable.findIndex((b) => b.opensAtMs === selectedBand?.opensAtMs));
  const onBandKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % pickable.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + pickable.length) % pickable.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = pickable.length - 1;
    if (next < 0) return;
    e.preventDefault();
    const group = e.currentTarget.parentElement;
    (group?.children[next] as HTMLElement | undefined)?.focus();
    if (!disabled) onSelectBand(pickable[next]!);
  };

  return (
    <div className="vt-avail-timeline">
      {bar}
      {tickRow}
      {noticeBand && noticeFirstStart != null ? (
        <p className="vt-avail-notice-label">
          earliest {formatHm(noticeFirstStart, tz)}
          {noticeMin > 0 ? ` · ${noticeReason(noticeMin)}` : ""}
        </p>
      ) : !noticeKnown && day === todayYmd && noticeMin > 0 ? (
        <p className="vt-avail-notice-label">Bookings today need {noticeReason(noticeMin)}.</p>
      ) : null}
      {pickable.length > 1 && !fixedStarts && (
        <div role="radiogroup" aria-label="Start windows" className="vt-avail-bandlist">
          {pickable.map((b, i) => {
            const t = bandText(b);
            const on = selectedBand?.opensAtMs === b.opensAtMs;
            return (
              <button
                key={b.opensAtMs}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={t.aria}
                tabIndex={i === selIndex ? 0 : -1}
                className="vt-avail-chip vt-avail-window"
                onClick={() => {
                  if (!disabled) onSelectBand(b);
                }}
                onKeyDown={(e) => onBandKey(e, i)}
              >
                {t.text}
              </button>
            );
          })}
        </div>
      )}
      <p className="vt-avail-caption">
        {shape === "multi" && entryCount > 1
          ? `Everyone starts together · longest ${formatLength(longestMin)}`
          : shape === "sessions"
            ? `Start times · ${lengthAdjective(longestMin)} session`
            : `Start times · ${formatLength(longestMin)}`}
      </p>
    </div>
  );
}
